<template>
  <div
    :class="[
      'ui-select',
      {
        'ui-select--error': error,
        'ui-select--opened': isOpened,
        'ui-select--disabled': disabled,
      },
    ]"
    v-click-outside="closeDropdown"
  >
    <div
      class="ui-select__trigger text-body"
      :tabindex="disabled ? -1 : 0"
      @click="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.esc="closeDropdown"
    >
      <span v-if="selectedLabel" class="ui-select__value">
        {{ selectedLabel }}
      </span>
      <span v-else class="ui-select__placeholder">
        {{ label }}
      </span>

      <span class="ui-select__arrow">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </span>
    </div>

    <transition name="fade">
      <ul v-if="isOpened" class="ui-select__options">
        <li
          v-for="(option, idx) in options"
          :key="idx"
          :class="[
            'ui-select__option',
            { 'ui-select__option--selected': option.value === value }
          ]"
          @click="selectOption(option)"
        >
          {{ option.label }}
        </li>
      </ul>
    </transition>

    <p v-if="error && errorMessage" class="ui-select__error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
export default {
  name: "UISelect",
  props: {
    label: {
      type: String,
      required: true,
    },
    // Сонголтууд: [{ label: 'Эрэгтэй', value: 'male' }, ...]
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    value: {
      type: [String, Number],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "Сонголт хийнэ үү",
    },
  },
  data() {
    return {
      isOpened: false,
    };
  },
  computed: {
    // Сонгосон байгаа утгын Label текстийг олж харуулах
    selectedLabel() {
      const selected = this.options.find(opt => opt.value === this.value);
      return selected ? selected.label : "";
    }
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpened = !this.isOpened;
    },
    closeDropdown() {
      this.isOpened = false;
    },
    selectOption(option) {
      this.$emit("input", option.value);
      this.$emit("change", option);
      this.isOpened = false;
    }
  },
  // Гадна талд дарах үед хаах туслах код (Танай төсөлд өөрөөр шийдсэн байж болно)
  directives: {
    clickOutside: {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style scoped>
.ui-select {
  position: relative;
  width: 100%;
  margin-bottom: 16px;
  box-sizing: border-box;
  user-select: none;
}

.ui-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 400;
  color: #1a1a2e;
  background-color: #ffffff;
  border: 1.5px solid #d0d5e0;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.ui-select__placeholder {
  color: #9ea8c0;
}

.ui-select__value {
  color: #1a1a2e;
}

.ui-select__arrow {
  display: flex;
  align-items: center;
  color: #9ea8c0;
  transition: transform 0.18s ease, color 0.18s ease;
}

/* Opened / Focused State */
.ui-select--opened .ui-select__trigger {
  border-color: #2d3f9c;
  box-shadow: 0 0 0 3px rgba(45, 63, 156, 0.1);
}

.ui-select--opened .ui-select__arrow {
  transform: rotate(180deg);
  color: #2d3f9c;
}

/* Error State */
.ui-select--error .ui-select__trigger {
  border-color: #e03131;
  box-shadow: 0 0 0 3px rgba(224, 49, 49, 0.1);
}

/* Disabled State */
.ui-select--disabled .ui-select__trigger {
  background-color: #f4f6fb;
  color: #9ea8c0;
  cursor: not-allowed;
  border-color: #d0d5e0;
  box-shadow: none;
}
.ui-select--disabled .ui-select__placeholder {
  color: #bfcaf0;
}

/* Options Dropdown List */
.ui-select__options {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 220px;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background-color: #ffffff;
  border: 1.5px solid #d0d5e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(26, 26, 46, 0.08);
  overflow-y: auto;
  z-index: 99;
  box-sizing: border-box;
}

.ui-select__option {
  padding: 10px 14px;
  font-size: 14px;
  color: #1a1a2e;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ui-select__option:hover {
  background-color: #f4f6fb;
  color: #2d3f9c;
}

.ui-select__option--selected {
  font-weight: 600;
  color: #2d3f9c;
  background-color: rgba(45, 63, 156, 0.05);
}

/* Error message */
.ui-select__error {
  margin: 6px 4px 0;
  font-size: 12px;
  color: #e03131;
}

/* Dropdown нээгдэх үеийн зөөлөн эффект */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>